
export type Locale = 'en' | 'cs';
export type Theme = 'system' | 'light' | 'dark';

// Added Plan enum for subscription levels
export enum Plan {
  FREE = 'FREE',
  PRO = 'PRO'
}

// Added SubscriptionStatus enum
export enum SubscriptionStatus {
  ACTIVE = 'ACTIVE',
  TRIAL = 'TRIAL',
  EXPIRED = 'EXPIRED'
}

// Added UserRole enum with values matching common usage in the app
export enum UserRole {
  ADMIN = 'admin',
  COORDINATOR = 'coordinator',
  VIEWER = 'viewer'
}

// Added User interface for identity management
export interface User {
  id: string;
  name: string;
  email: string;
  language: Locale;
}

// Added Profile interface which extends User and adds theme preference
export interface Profile extends User {
  theme?: Theme;
  username?: string; // Username for login (required for new users)
}

export interface Wedding {
  id: string;
  title: string;
  date: string;
  location: string;
  default_language: Locale;
  public_enabled: boolean;
  public_slug: string;
  welcome_message?: string;
  owner_user_id: string;
  plan: Plan;
  subscriptionStatus: SubscriptionStatus;
  is_free_tier: boolean;
  public_welcome_message: string;
  publicConfig: {
    showSchedule: boolean;
    showFaq: boolean;
    welcomeMessage: string;
    brideName?: string;
    groomName?: string;
    bridePhotoUrl?: string;
    groomPhotoUrl?: string;
    ceremonyTime?: string;
    ceremonyVenue?: string;
    fullAddress?: string;
    mapsUrl?: string;
    dressCode?: string;
    heroImageUrl?: string;
    storyText?: string;
    showCatering?: boolean;
    cateringIntro?: string;
    cateringShowMain?: boolean;
    cateringMainText?: string;
    cateringShowWarmBuffet?: boolean;
    cateringWarmBuffetText?: string;
    cateringShowColdBuffet?: boolean;
    cateringColdBuffetText?: string;
    cateringShowSweetBar?: boolean;
    cateringSweetBarText?: string;
    cateringShowDrinks?: boolean;
    cateringDrinksText?: string;
    cateringMainPhotoUrl?: string;
    cateringWarmPhotoUrl?: string;
    cateringColdPhotoUrl?: string;
    cateringSweetPhotoUrl?: string;
    cateringDrinksPhotoUrl?: string;
    showMusic?: boolean;
    musicIntro?: string;
    musicLiveDj?: boolean;
    musicFromSpeakers?: boolean;
    musicSpotifyUrl?: string;
    musicPlaylistCta?: string;
    showEntertainment?: boolean;
    entertainmentIntro?: string;
    showAccommodationInfo?: boolean;
    accommodationInfo?: string;
    accommodationMapsUrl?: string;
    showGuestDetailsForm?: boolean;
    guestFormIntro?: string;
    guestFormShowAccommodation?: boolean;
    guestFormShowAllergies?: boolean;
    guestFormShowChildren?: boolean;
    guestFormShowGuestNote?: boolean;
  };
  vendor_types: string[];
}

export interface WeddingMember {
  wedding_id: string;
  user_id: string;
  role: UserRole;
}

/** Role v seznamu hostů. bride/groom jsou spravované pouze v Sekci „Svatební tým“. */
export type GuestRole = 'guest' | 'witness' | 'bridesmaid' | 'best_man' | 'parent' | 'wedding_coordinator' | 'bride' | 'groom';

/** Role, které lze zvolit při „Přidat hosta“ (bez bride/groom). */
export type GuestRoleInGuestsList = 'guest' | 'witness' | 'bridesmaid' | 'best_man' | 'parent' | 'wedding_coordinator';

export type GuestCategory = 'guest' | 'wedding_team';

export interface Guest {
  id: string;
  wedding_id: string;
  full_name: string;
  rsvp_status: 'pending' | 'confirmed' | 'declined';
  plus_one: boolean;
  kids_count: number;
  side: 'bride' | 'groom' | 'mutual';
  role: GuestRole;
  /** Započítává se do celkového počtu osob (strava, kapacity). */
  counts_for_capacity?: boolean;
  /** Zobrazuje se v sekci Hosté. Nevěsta/ženich = false. */
  visible_in_guests?: boolean;
  /** guest = běžný host/koordinátor, wedding_team = nevěsta/ženich. */
  category?: GuestCategory;
  contact_email?: string;
  contact_phone?: string;
  allergies?: string;
  notes?: string;
  created_at?: string;
}

/** Číselník typů vazeb mezi hosty (na svatbu, spravuje se v Nastavení). */
export interface GuestRelationshipType {
  id: string;
  wedding_id: string;
  label: string;
  sort_order: number;
  created_at?: string;
}

/** Jedna vazba mezi dvěma hosty (v DB platí guest_low_id < guest_high_id). */
export interface GuestRelationshipLink {
  id: string;
  wedding_id: string;
  relationship_type_id: string;
  guest_low_id: string;
  guest_high_id: string;
  created_at?: string;
}

/** Minimální payload pro úpravu záznamu ve Svatebním týmu (nevěsta, ženich, koordinátor). */
export interface WeddingTeamMemberPayload {
  full_name: string;
  contact_email?: string;
  contact_phone?: string;
  allergies?: string;
  notes?: string;
}

export interface WeddingTeamResponse {
  bride: Guest | null;
  groom: Guest | null;
  coordinator: Guest | null;
}

export interface Task {
  id: string;
  wedding_id: string;
  title: string;
  description?: string; // Added description
  phase: 'early' | 'mid' | 'approaching' | 'final' | 'last_week' | 'today' | 'post_1_day' | 'post_1_week' | 'post_1_month';
  due_date: string;
  status: 'todo' | 'in_progress' | 'done';
  assigned_to?: string; // ID of the guest assigned
  notes?: string;
  created_at?: string;
}

export interface ScheduleItem {
  id: string;
  wedding_id: string;
  start_time: string;
  end_time?: string;
  title: string;
  location?: string;
  notes?: string;
  is_public: boolean;
  source?: 'schedule' | 'activity';
  /** Pokud vyplněno, položka pochází z Obřadu – upravuje se pouze v sekci Svatební obřad. */
  ceremony_step_id?: string | null;
}

export interface Activity {
  id: string;
  wedding_id: string;
  title: string;
  description?: string;
  type: 'planned' | 'proposal';
  status: 'draft' | 'approved' | 'rejected';
  is_all_day: boolean;
  start_time?: string;
  end_time?: string;
  votes_up: number;
  votes_down: number;
  created_at?: string;
}

export interface BudgetItem {
  id: string;
  wedding_id: string;
  category: string;
  title: string;
  planned_amount: number;
  actual_amount: number; // Kept for legacy/compatibility, generally same as paid or final
  paid_amount: number;
  due_date?: string;
  // Source of Truth fields
  source_type: 'vendor' | 'ring' | 'ceremony' | 'manual';
  source_id?: string;
  include_in_budget: boolean;
}

export interface VendorFile {
  name: string;
  path: string;
}

export interface Vendor {
  id: string;
  wedding_id: string;
  type: string;
  name: string;
  contact_name?: string;
  contact_email?: string;
  contact_phone?: string;
  price_estimate?: number;
  paid_amount?: number; // Added for budget tracking
  status: 'new' | 'potential' | 'contacted' | 'booked' | 'canceled';
  notes?: string;
  /** Up to 3 file attachments stored as JSONB array */
  files?: VendorFile[];
}

export interface Table {
  id: string;
  wedding_id: string;
  name: string;
  capacity: number;
}

export interface TableAssignment {
  wedding_id: string;
  table_id: string;
  guest_id: string;
}

export interface Note {
  id: string;
  wedding_id: string;
  title: string;
  content: string;
  tags: string[];
  links: string[];
  is_public: boolean;
  created_at?: string;
}

export interface Template {
  id: string;
  wedding_id: string;
  title: string;
  content: string;
  language: Locale;
  tags: string[];
  created_at?: string;
}

export interface Ring {
  id: string;
  wedding_id: string;
  owner: 'nevesta' | 'zenich';
  ring_type: 'snubni' | 'zasnubni' | 'jiny';
  style_name: string;
  material: string;
  size?: string;
  engraving_text?: string;
  vendor_name?: string;
  vendor_contact?: string;
  price_estimate: number;
  price_actual: number; // Final agreed price
  paid_amount?: number; // Added for budget tracking
  status: 'confirmed' | 'potential' | 'canceled';
  ordered_at?: string;
  expected_delivery?: string;
  notes?: string;
  created_at?: string;
}

export interface CeremonyMeta {
  id: string;
  wedding_id: string;
  ceremony_type: 'civilni' | 'cirkevni' | 'symbolicky';
  date: string;
  start_time: string;
  location_name: string;
  location_address?: string;
  indoor_outdoor: 'indoor' | 'outdoor';
  plan_b_weather?: string;
  notes?: string;
}

export interface CeremonyContact {
  id: string;
  wedding_id: string;
  role_type: 'oddavajici' | 'matrikarka' | 'knez' | 'koordinator' | 'spravce_mista' | 'hudba' | 'fotograf' | 'jiny';
  name: string;
  phone?: string;
  email?: string;
  org?: string; // urad/cirkev
  status: 'confirmed' | 'potential' | 'canceled';
  notes?: string;
  price_estimate?: number;
  paid_amount?: number; // Added for budget tracking
}

export interface CeremonyRole {
  id: string;
  wedding_id: string;
  role_key: string; 
  person_name: string;
  relationship?: string;
  instructions?: string;
  notes?: string;
}

export interface CeremonyStep {
  id: string;
  wedding_id: string;
  order_index: number;
  step_name: string;
  description: string;
  participants: string[];
  duration_minutes?: number;
  cue?: string;
  notes?: string;
  /** Čas konání (HH:mm). Synchronizuje se do Harmonogramu dne. */
  start_time?: string;
}

export interface CeremonyChecklist {
  id: string;
  wedding_id: string;
  title: string;
  status: 'todo' | 'done';
  due_date?: string;
  notes?: string;
}

export interface CeremonyMusic {
  id: string;
  wedding_id: string;
  moment: string; 
  track_name: string;
  source_link?: string;
  responsible_person?: string;
  notes?: string;
}
